import * as React from "react";
import { observer, MobXProviderContext } from "mobx-react";
import { UitkNewDatePicker } from "uitk-react-new-date-picker";
import { formatText, formatDateString } from "bernie-l10n";
import { Link as RouterLink } from "react-router-dom";
import { DialogRoute, RouteStore } from "bernie-core";
import { Layer } from "bernie-view";
import { DatesProps } from "./typings";
import { DateFieldConfig } from "src/stores/wizard/config";
import { FlightWizardState } from "src/stores/wizard/state/flight";
import { PackageWizardState } from "src/stores/wizard/state/package";
import { DateState } from "src/stores/wizard/state/global";
import { Context } from "bernie-context";

export const Dates: React.FunctionComponent<DatesProps> = observer(
  ({ singleDate, classes, lobState, isPartialStay, multiLegIndex }) => {
    const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
    const context: Context = React.useContext(MobXProviderContext).context;
    const [showDatePicker, setShowDatePicker] = React.useState<boolean>(false);

    const [dateConfig, updateDates, dateState, dateStartInvalidKey, dateEndInvalidKey] = ((): [
      DateFieldConfig,
      (start: Date, end: Date, multiLegIndex?: number) => void,
      DateState,
      string,
      string
    ] => {
      if (isPartialStay && lobState instanceof PackageWizardState) {
        return [
          lobState.config.partialStayDate,
          lobState.updatePartialStayDateSelection,
          lobState.partialStayDate,
          lobState.partialStayStartDateInvalidKey,
          lobState.partialStayEndDateInvalidKey
        ];
      } else if (lobState instanceof PackageWizardState && lobState.subLOBState === "hc") {
        return [
          lobState.config.subLOB.hotelCar.date,
          lobState.updateDateSelection,
          lobState.date,
          lobState.dateStartInvalidKey,
          lobState.dateEndInvalidKey
        ];
      } else if (isPartialStay && lobState instanceof FlightWizardState) {
        return [
          lobState.config.hotelDate,
          lobState.updateHotelDateSelection,
          lobState.hotelDate,
          lobState.hotelStartDateInvalidKey,
          lobState.hotelEndDateInvalidKey
        ];
      } else if (
        multiLegIndex !== undefined &&
        lobState instanceof FlightWizardState &&
        lobState.config.multiCityFlight
      ) {
        return [
          lobState.config.multiCityFlight.legs[multiLegIndex].date,
          lobState.updateLegDate,
          lobState.listOfAdditionalLegs[multiLegIndex].date,
          "",
          ""
        ];
      } else {
        return [
          lobState.config.date,
          lobState.updateDateSelection,
          lobState.date,
          lobState.dateStartInvalidKey,
          lobState.dateEndInvalidKey ? lobState.dateEndInvalidKey : ""
        ];
      }
    })();

    const onOpenCB = () => {
      setShowDatePicker(true);
    };

    const onSubmitCB = (start: Date, end: Date, isMobile: boolean) => {
      if (isMobile) {
        routeStore.updateSearch("datePickerDialog");
      }

      setShowDatePicker(false);

      if (end === undefined) {
        end = dateState.end;
      }

      if (start > end) {
        end.setDate(start.getDate() + 1);
      }

      updateDates(start, end, multiLegIndex);
    };

    const onDismissCB = (isMobile: boolean) => {
      if (isMobile) {
        routeStore.closeDialog();
      }

      setShowDatePicker(false);
    };

    const pwaDialog = `datepicker-${dateConfig.dialogId}`;

    const routerLinkWrapper = (children: React.ReactNode) => {
      return (
        <RouterLink
          to={{
            pathname: routeStore.pathname,
            search: routeStore.mergeQueryString(routeStore.search, { pwaDialog })
          }}
        >
          {children}
        </RouterLink>
      );
    };

    const dialogWrapper = (children: React.ReactNode, dateType: string) => {
      return (
        <DialogRoute id={pwaDialog}>
          <Layer id={`${pwaDialog}-${dateType}`} onDismiss={onDismissCB}>
            {children}
          </Layer>
        </DialogRoute>
      );
    };

    const dateFieldProps = {
      dialogWrapper,
      endId: "endDummy", // TODO: uitk-new-date-picker not supports setting custom date value format.
      endInvalid: formatText(dateEndInvalidKey, dateConfig.maxDaysRange),
      endLabel: formatText(dateConfig.end.labelToken),
      endName: "endDummy", // TODO: uitk-new-date-picker not supports setting custom date value format.
      endPlaceholder: formatText(dateConfig.end.labelToken),
      routerLinkWrapper,
      showDatePicker,
      startId: "startDummy", // TODO: uitk-new-date-picker not supports setting custom date value format.
      startInvalid: formatText(dateStartInvalidKey, dateConfig.maxDaysRange),
      startLabel: formatText(dateConfig.start.labelToken),
      startName: "startDummy", // TODO: uitk-new-date-picker not supports setting custom date value format.
      startPlaceholder: formatText(dateConfig.start.labelToken)
    };

    // Certain LOBs dont support ISO format for their SRPs they only support local format
    // If that is the case the local raw format will be taken from bernie context
    const rawFormatForSubmission = dateConfig.ISOSubmissionSupport
      ? dateConfig.format
      : context.localDateFormat;

    return (
      <div className={classes}>
        {/*
      TODO: uitk-new-date-picker not supports setting custom date value format.
      Add start/end hidden inputs with custom value format.
      */}
        <input
          type="hidden"
          name={dateConfig.queryParam.start}
          value={formatDateString(dateState.start, { raw: rawFormatForSubmission })}
        />
        {!singleDate && (
          <input
            type="hidden"
            name={dateConfig.queryParam.end}
            value={formatDateString(dateState.end, { raw: rawFormatForSubmission })}
          />
        )}
        <UitkNewDatePicker
          dateFieldProps={dateFieldProps}
          daysBookableInAdvance={dateConfig.daysBookableInAdvance}
          doubleMonths={true}
          endDate={singleDate ? "" : formatDateString(dateState.end, { raw: dateConfig.format })}
          firstDayOfWeek={dateConfig.firstDayOfWeek}
          midnightBookingOffset={dateConfig.midnightBookingOffset}
          onDismissCB={onDismissCB}
          onOpenCB={onOpenCB}
          onSubmitCB={onSubmitCB}
          startDate={formatDateString(dateState.start, { raw: dateConfig.format })}
          singleMode={singleDate}
        />
      </div>
    );
  }
);
